import {ModelSortDirection} from "../API";

type Sortable = string | number | boolean | null | undefined;

export const sortByField = <T extends Record<string, any>>(
    array: T[],
    field: string,
    order: ModelSortDirection = ModelSortDirection.ASC,
    nullsFirst: boolean = false,
    getFilterableValue: (field: string, obj: T) => Sortable = (field, obj: T) => obj[field] as Sortable
) => {
    return [...array].sort((a, b) => {
        const valA = getFilterableValue(field, a);
        const valB = getFilterableValue(field, b);

        // Handle null/undefined values explicitly so that `false` and `0` are treated as valid values
        const aIsNull = valA === null || valA === undefined;
        const bIsNull = valB === null || valB === undefined;
        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return nullsFirst ? -1 : 1;
        if (bIsNull) return nullsFirst ? 1 : -1;

        // Determine the comparison based on type
        if (typeof valA === 'string' && typeof valB === 'string') {
            return order === ModelSortDirection.ASC
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
        }

        // Numeric comparison
        if (typeof valA === 'number' && typeof valB === 'number') {
            return order === ModelSortDirection.ASC ? valA - valB : valB - valA;
        }

        // Boolean comparison (false comes first in ASC order)
        if (typeof valA === 'boolean' && typeof valB === 'boolean') {
            if (valA === valB) return 0;
            return order === ModelSortDirection.ASC
                ? (valA ? 1 : -1) // false comes before true in ASC
                : (valA ? -1 : 1); // true comes before false in DESC
        }


        // Fallback for mixed types (convert to string comparison)
        return order === ModelSortDirection.ASC
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
    });
}
